import type { ApiRequester } from "@/shared/api";

export class UsersApi {
  constructor(private readonly api: ApiRequester) {}

  list(
    payload: iSharedUser.ListUsersPayloadDto = {}
  ): Promise<iSharedUser.ListUsersResponseDto> {
    const search = new URLSearchParams();
    if (payload.limit !== undefined) search.set("limit", String(payload.limit));
    if (payload.offset !== undefined)
      search.set("offset", String(payload.offset));

    return this.api.get<iSharedUser.ListUsersResponseDto>({
      path: `/users${
        search.size ? `?${search.toString()}` : ""
      }` as `/${string}`,
      commit: "users/setUsers",
    });
  }

  listRoles(): Promise<iSharedUser.ListRolesResponseDto> {
    return this.api.get<iSharedUser.ListRolesResponseDto>({
      path: "/users/roles",
      commit: "users/setRoles",
    });
  }

  listPermissions(): Promise<iSharedUser.ListPermissionsResponseDto> {
    return this.api.get<iSharedUser.ListPermissionsResponseDto>({
      path: "/users/permissions",
      commit: "users/setPermissions",
    });
  }

  create(
    payload: iSharedUser.CreateUserPayloadDto
  ): Promise<iSharedUser.CreateUserResponseDto> {
    return this.api.post<
      iSharedUser.CreateUserResponseDto,
      iSharedUser.CreateUserPayloadDto
    >({
      path: "/users",
      payload,
      commit: "users/addUser",
    });
  }

  update(
    payload: iSharedUser.UpdateUserPayloadDto
  ): Promise<iSharedUser.UpdateUserResponseDto> {
    return this.api.patch<
      iSharedUser.UpdateUserResponseDto,
      iSharedUser.UpdateUserPayloadDto
    >({
      path: "/users",
      payload,
      commit: "users/updateUser",
    });
  }

  delete(
    payload: iSharedUser.DeleteUserPayloadDto
  ): Promise<iSharedUser.DeleteUserResponseDto> {
    return this.api.delete<
      iSharedUser.DeleteUserResponseDto,
      iSharedUser.DeleteUserPayloadDto
    >({
      path: "/users",
      payload,
      commit: "users/deleteUser",
    });
  }

  changePassword(
    payload: iSharedUser.ChangeUserPasswordPayloadDto
  ): Promise<iSharedUser.ChangeUserPasswordResponseDto> {
    return this.api.patch<
      iSharedUser.ChangeUserPasswordResponseDto,
      iSharedUser.ChangeUserPasswordPayloadDto
    >({
      path: "/users/password",
      payload,
    });
  }

  createRole(
    payload: iSharedUserRole.CreateRolePayloadDto
  ): Promise<iSharedUserRole.CreateRoleResponseDto> {
    return this.api.post<
      iSharedUserRole.CreateRoleResponseDto,
      iSharedUserRole.CreateRolePayloadDto
    >({
      path: "/users/roles",
      payload,
      commit: "users/addRole",
    });
  }

  updateRole(
    payload: iSharedUserRole.UpdateRolePayloadDto
  ): Promise<iSharedUserRole.UpdateRoleResponseDto> {
    return this.api.patch<
      iSharedUserRole.UpdateRoleResponseDto,
      iSharedUserRole.UpdateRolePayloadDto
    >({
      path: "/users/roles",
      payload,
      commit: "users/updateRole",
    });
  }

  deleteRole(
    payload: iSharedUserRole.DeleteRolePayloadDto
  ): Promise<iSharedUserRole.DeleteRoleResponseDto> {
    return this.api.delete<
      iSharedUserRole.DeleteRoleResponseDto,
      iSharedUserRole.DeleteRolePayloadDto
    >({
      path: "/users/roles",
      payload,
      commit: "users/deleteRole",
    });
  }

  updateRolePermissions(
    payload: iSharedUserRole.UpdateRolePermissionsPayloadDto
  ): Promise<iSharedUserRole.UpdateRolePermissionsResponseDto> {
    return this.api.patch<
      iSharedUserRole.UpdateRolePermissionsResponseDto,
      iSharedUserRole.UpdateRolePermissionsPayloadDto
    >({
      path: "/users/roles/permissions",
      payload,
      commit: "users/updateRole",
    });
  }

  transferSuperadministrator(
    payload: iSharedUser.TransferSuperadministratorPayloadDto
  ): Promise<iSharedUser.TransferSuperadministratorResponseDto> {
    return this.api.post<
      iSharedUser.TransferSuperadministratorResponseDto,
      iSharedUser.TransferSuperadministratorPayloadDto
    >({
      path: "/users/superadministrator/transfer",
      payload,
    });
  }

  listDepartments(
    payload: iSharedUser.ListDepartmentsPayloadDto = {}
  ): Promise<iSharedUser.ListDepartmentsResponseDto> {
    const query = new URLSearchParams();
    if (payload.limit !== undefined) query.set("limit", String(payload.limit));
    if (payload.offset !== undefined) query.set("offset", String(payload.offset));
    return this.api.get<iSharedUser.ListDepartmentsResponseDto>({
      path: `/departments${query.size ? `?${query.toString()}` : ""}`,
    });
  }

  createDepartment(
    payload: iSharedUser.CreateDepartmentPayloadDto
  ): Promise<iSharedUser.DepartmentDto> {
    return this.api.post<
      iSharedUser.DepartmentDto,
      iSharedUser.CreateDepartmentPayloadDto
    >({ path: "/departments", payload });
  }

  updateDepartment(
    payload: iSharedUser.UpdateDepartmentPayloadDto
  ): Promise<iSharedUser.DepartmentDto> {
    return this.api.patch<
      iSharedUser.DepartmentDto,
      iSharedUser.UpdateDepartmentPayloadDto
    >({ path: "/departments", payload });
  }

  deleteDepartment(
    payload: iSharedUser.DeleteDepartmentPayloadDto
  ): Promise<iSharedUser.DeleteDepartmentResponseDto> {
    return this.api.delete<
      iSharedUser.DeleteDepartmentResponseDto,
      iSharedUser.DeleteDepartmentPayloadDto
    >({ path: "/departments", payload });
  }
}
